<template>
    <div class="hwk-page">
        <!-- 头部 -->
        <header class="hwk-hero">
            <div class="hero-row">
                <div>
                    <p class="hero-hi">作业</p>
                    <p class="hero-sub">{{ subtitle }}</p>
                </div>
                <span class="seg">
                    <i :class="{ on: range === 'today' }" @click="switchRange('today')">今天</i>
                    <i :class="{ on: range === 'week' }" @click="switchRange('week')">本周</i>
                </span>
            </div>
        </header>

        <main class="hwk-body">
            <!-- 统计 -->
            <section class="stat-card">
                <div class="st-item">
                    <p class="st-label">待交</p>
                    <p class="st-num c-amber">{{ stat.todo }}</p>
                </div>
                <div class="st-item">
                    <p class="st-label">已交</p>
                    <p class="st-num c-green">{{ stat.done }}</p>
                </div>
                <div class="st-item">
                    <p class="st-label">逾期</p>
                    <p class="st-num c-red">{{ stat.overdue }}</p>
                </div>
            </section>

            <!-- 筛选 -->
            <div class="filter">
                <span
                    class="filter-item"
                    v-for="f in filters"
                    :key="f.key"
                    :class="{ on: filter === f.key }"
                    @click="filter = f.key"
                >
                    {{ f.text }}<em v-if="f.count">{{ f.count }}</em>
                </span>
            </div>

            <!-- 列表 -->
            <div class="list">
                <div class="item" v-for="it in list" :key="it.id" @click="openItem(it)">
                    <div class="it-badge" :style="{ background: it.meta.bg, color: it.meta.fg }">
                        {{ it.meta.short }}
                    </div>
                    <div class="it-main">
                        <p class="it-title">{{ it.title }}</p>
                        <p class="it-note">{{ it.due }} · {{ it.note }}</p>
                    </div>
                    <span
                        class="it-status"
                        :style="{ background: it.statusMeta.bg, color: it.statusMeta.fg }"
                    >
                        {{ it.statusMeta.text }}
                    </span>
                </div>

                <p class="empty" v-if="!list.length">这一类暂时没有作业</p>
            </div>

            <p class="tip">演示数据 · 接入作业记录后自动同步</p>
        </main>

        <p class="toast-tip" v-if="toast">{{ toast }}</p>
    </div>
</template>

<script>
import { fetchHomework, homeworkStats, isTodo } from '../../utils/studyData';

export default {
    name: 'AppHomework',
    data() {
        return {
            range: 'today',
            filter: 'all',
            homework: [],
            loading: false,
            toast: '',
            toastTimer: null
        };
    },
    computed: {
        stat() {
            return homeworkStats(this.homework);
        },
        subtitle() {
            if (this.loading) {
                return '正在加载作业…';
            }
            if (!this.homework.length) {
                return '这一类暂时没有作业';
            }
            if (this.stat.todo) {
                return '还有 ' + this.stat.todo + ' 项没交';
            }
            if (this.stat.overdue) {
                return '有 ' + this.stat.overdue + ' 项已逾期';
            }
            return '全部交齐了，挺好';
        },
        filters() {
            return [
                { key: 'all', text: '全部', count: this.homework.length },
                { key: 'todo', text: '待交', count: this.stat.todo },
                { key: 'done', text: '已交', count: this.stat.done },
                { key: 'overdue', text: '逾期', count: this.stat.overdue }
            ];
        },
        list() {
            const key = this.filter;
            if (key === 'all') {
                return this.homework;
            }
            if (key === 'todo') {
                return this.homework.filter(isTodo);
            }
            return this.homework.filter(it => it.status === key);
        }
    },
    created() {
        this.load(this.range);
    },
    beforeDestroy() {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    },
    methods: {
        load(range) {
            this.loading = true;
            fetchHomework(range)
                .then(list => {
                    this.homework = list || [];
                })
                .catch(() => {
                    this.homework = [];
                })
                .then(() => {
                    this.loading = false;
                });
        },
        switchRange(range) {
            if (this.range === range) {
                return;
            }
            this.range = range;
            this.filter = 'all';
            this.load(range);
        },
        openItem(it) {
            if (!it || !it.id) {
                this.showToast('这条作业数据不完整');
                return;
            }
            this.$router.push({ path: '/hwk/task/' + it.id });
        },
        showToast(text) {
            this.toast = text;
            if (this.toastTimer) {
                clearTimeout(this.toastTimer);
            }
            this.toastTimer = setTimeout(() => {
                this.toast = '';
            }, 1800);
        }
    }
};
</script>

<style lang="less" scoped>
.hwk-page {
    min-height: 100vh;
    box-sizing: border-box;
    padding-bottom: calc(150px + constant(safe-area-inset-bottom));
    padding-bottom: calc(150px + env(safe-area-inset-bottom));
    background: #f4f6fa;
}

/* 头部 */
.hwk-hero {
    position: relative;
    padding: 56px 40px 60px;
    color: #fff;
    background: var(--blueTheme, #0066b3);
    border-radius: 0 0 44px 44px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -150px;
        right: -100px;
        width: 380px;
        height: 380px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.13);
    }
}

.hero-row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hero-hi {
    margin: 0;
    font-size: 44px;
    font-weight: 600;
    letter-spacing: 1px;
}

.hero-sub {
    margin: 16px 0 0;
    font-size: 24px;
    opacity: 0.84;
}

.seg {
    flex: none;
    display: flex;
    padding: 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.2);

    i {
        padding: 8px 24px;
        border-radius: 999px;
        font-style: normal;
        font-size: 22px;
        color: rgba(255, 255, 255, 0.86);

        &.on {
            color: var(--blueTheme, #0066b3);
            background: #fff;
        }
    }
}

/* 统计卡 */
.hwk-body {
    padding: 0 30px;
}

.stat-card {
    position: relative;
    z-index: 2;
    display: flex;
    margin-top: -36px;
    padding: 28px 8px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.08);
}

.st-item {
    position: relative;
    flex: 1;
    min-width: 0;
    text-align: center;

    & + .st-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 4px;
        bottom: 4px;
        width: 1px;
        background: #eef1f6;
    }
}

.st-label {
    margin: 0;
    font-size: 22px;
    color: #8a94a6;
}

.st-num {
    margin: 8px 0 0;
    font-size: 40px;
    font-weight: 600;

    &.c-amber {
        color: #ba7517;
    }
    &.c-green {
        color: #0f6e56;
    }
    &.c-red {
        color: #a32d2d;
    }
}

/* 筛选 */
.filter {
    display: flex;
    margin: 28px 0 18px;
    overflow-x: auto;

    &::-webkit-scrollbar {
        display: none;
    }
}

.filter-item {
    flex: none;
    margin-right: 16px;
    padding: 12px 28px;
    border-radius: 999px;
    font-size: 24px;
    color: #5f6b7d;
    background: #fff;
    border: 1px solid #e8ecf2;

    em {
        margin-left: 8px;
        font-style: normal;
        font-size: 20px;
        color: #a3abba;
    }

    &.on {
        color: #fff;
        background: var(--blueTheme, #0066b3);
        border-color: var(--blueTheme, #0066b3);

        em {
            color: rgba(255, 255, 255, 0.8);
        }
    }
}

/* 列表 */
.list {
    padding: 0 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.item {
    display: flex;
    align-items: center;
    padding: 28px 0;

    & + .item {
        border-top: 1px solid #f2f4f8;
    }
    &:active {
        opacity: 0.85;
    }
}

.it-badge {
    flex: none;
    width: 56px;
    height: 56px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 600;
}

.it-main {
    flex: 1;
    min-width: 0;
    margin-left: 22px;
}

.it-title {
    margin: 0;
    font-size: 28px;
    color: #2b3445;
}

.it-note {
    margin: 8px 0 0;
    font-size: 22px;
    color: #8a94a6;
}

.it-status {
    flex: none;
    margin-left: 16px;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 20px;
}

.empty {
    padding: 60px 0;
    text-align: center;
    font-size: 22px;
    color: #a3abba;
}

.tip {
    margin: 30px 0 0;
    text-align: center;
    font-size: 20px;
    color: #c2c9d6;
}

/* 轻提示 */
.toast-tip {
    position: fixed;
    left: 50%;
    bottom: calc(200px + constant(safe-area-inset-bottom));
    bottom: calc(200px + env(safe-area-inset-bottom));
    z-index: 600;
    transform: translateX(-50%);
    padding: 20px 36px;
    border-radius: 999px;
    font-size: 24px;
    color: #fff;
    background: rgba(17, 24, 39, 0.86);
    white-space: nowrap;
}
</style>
